import { createSlice } from "@reduxjs/toolkit";
import { Steps, OrderPayMoreSteps } from "pages/OrderOverview/utils/constants";
import { Steps as OrderUpdateSteps } from "pages/OrderUpdate/utils/constants";
import type { RootState } from "store";
import { IAccount } from "types/Account/Account";
import { IIntegrationError, IOrder } from "types/Order/Order";

interface IOrderStateProps {
  viewOrderViewReceipt: boolean;
  overviewStep: Steps;
  orderUpdateStep: OrderUpdateSteps;
  orderPayMoreStep: OrderPayMoreSteps;
  accountInfo: IAccount;
  payersNameInfo: {
    name: string;
    value: string;
  }[];
  payersCodesInfo: {
    name: string;
    code: string[];
  }[];
  payersTypeInfo: {
    name: string;
    type: string;
  }[];
  selectedDraftOrder: IOrder;
  selectedOrderToRedo: IOrder;
  selectedOrderToCopy: IOrder;
  integrationErrors: IIntegrationError[];
  stripeClientSecret: string;
}

const initialState: IOrderStateProps = {
  viewOrderViewReceipt: false,
  overviewStep: Steps.DOB_AUTH,
  orderUpdateStep: OrderUpdateSteps.DOB_AUTH,
  orderPayMoreStep: OrderPayMoreSteps.DOB_AUTH,
  accountInfo: {} as IAccount,
  payersNameInfo: [],
  payersCodesInfo: [],
  payersTypeInfo: [],
  selectedDraftOrder: {} as IOrder,
  selectedOrderToRedo: {} as IOrder,
  selectedOrderToCopy: {} as IOrder,
  integrationErrors: [],
  stripeClientSecret: "",
};

export const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    onChangeViewOrderViewReceipt: (state, action: { payload: boolean }) => {
      state.viewOrderViewReceipt = action.payload;
    },
    // CLE-2181: patient tokens are now order-bound and expire. When the API rejects one, send the
    // patient back to the date-of-birth gate instead of leaving them on a page whose requests all
    // fail — every one of these step machines starts at DOB_AUTH.
    onPatientSessionReset: (state) => {
      state.overviewStep = Steps.DOB_AUTH;
      state.orderUpdateStep = OrderUpdateSteps.DOB_AUTH;
      state.orderPayMoreStep = OrderPayMoreSteps.DOB_AUTH;
    },
    onOrderOverviewStepChange: (
      state,
      action: { payload: { step: Steps } }
    ) => {
      state.overviewStep = action.payload.step;
    },
    onOrderUpdateStepChange: (
      state,
      action: { payload: { step: OrderUpdateSteps } }
    ) => {
      state.orderUpdateStep = action.payload.step;
    },
    onOrderPayMoreStepChange: (
      state,
      action: { payload: { step: OrderPayMoreSteps } }
    ) => {
      state.orderPayMoreStep = action.payload.step;
    },
    onChangeAccountInfo: (
      state,
      action: { payload: { accountInfo: IAccount } }
    ) => {
      state.accountInfo = action.payload.accountInfo;
    },
    onPayerNameLoad: (state, action) => {
      state.payersNameInfo = action.payload;
    },
    onPayerCodeLoad: (state, action) => {
      state.payersCodesInfo = action.payload;
    },
    onPayerTypeLoad: (state, action) => {
      state.payersTypeInfo = action.payload;
    },
    onSelectDraftOrder: (state, action: { payload: IOrder }) => {
      state.selectedDraftOrder = action.payload;

      state.integrationErrors = Object.entries(
        action.payload.integrationErrors ?? {}
      ).map((aError) => {
        return {
          errorType: aError[0],
          message: aError[1],
          active: true,
        };
      });
    },
    onSelectOrderToRedo: (state, action: { payload: IOrder }) => {
      state.selectedOrderToRedo = action.payload;
    },
    onSelectOrderToCopy: (state, action: { payload: IOrder }) => {
      state.selectedOrderToCopy = action.payload;
    },
    onIntegrationChange: (
      state,
      action: {
        payload: IIntegrationError;
      }
    ) => {
      const remainingErrors = state.integrationErrors.filter((aError) => {
        return aError.errorType !== action.payload.errorType;
      });
      state.integrationErrors = [...remainingErrors, action.payload];
    },
    onStripeClientSecretLoad: (state, action) => {
      state.stripeClientSecret = action.payload;
    },
  },
});

export const {
  onPatientSessionReset,
  onOrderOverviewStepChange,
  onOrderUpdateStepChange,
  onOrderPayMoreStepChange,
  onChangeAccountInfo,
  onChangeViewOrderViewReceipt,
  onPayerNameLoad,
  onPayerCodeLoad,
  onPayerTypeLoad,
  onSelectDraftOrder,
  onSelectOrderToRedo,
  onSelectOrderToCopy,
  onIntegrationChange,
  onStripeClientSecretLoad,
} = orderSlice.actions;

export const orderSelector = (state: RootState) => state.orderSlice;

export default orderSlice.reducer;
